import React, { useState, useEffect, useCallback } from 'react';
import UserManagement from './UserManagement';
import TradingAccounts from './TradingAccounts';
import Symbols from './Symbols';
import UserTree from './UserTree';
import Trades from './Trades';
import Simulator from './Simulator';
import Simulator10s from './Simulator10s';
import Technicals from './Technicals';
import apiService from '../services/api';

const Dashboard = ({ 
  onLogout, 
  currentUser, 
  onCreateUser, 
  onGetUsersList, 
  onUpdateUser,
  onCreateTradingAccount,
  onGetTradingAccountList,
  onGetTradingAccountListAll,
  onDeleteTradingAccount,
  onCreateSymbol,
  onUpdateSymbol,
  onGetSymbolList,
  onGetSymbolListAll,
  onDeleteSymbol
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [tradingData, setTradingData] = useState({
    totalProfit: 0,
    dailyProfit: 0,
    activeBots: 0,
    totalTrades: 0,
    winRate: 0,
    balance: 0
  });

  const [dashboardStats, setDashboardStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(currentUser?.userId || null);

  const fetchDashboardStats = useCallback(async () => {
    try {
      const res = await apiService.getDashboardStats({ userId: selectedUserId || currentUser?.userId });
      setDashboardStats(res);
    } catch (e) {}
  }, [currentUser, selectedUserId]);

  const loadUsers = useCallback(async () => {
    try {
      if (typeof onGetUsersList === 'function') {
        const { success, data } = await onGetUsersList({});
        if (success && Array.isArray(data)) {
          setUsers(data);
        }
      }
    } catch (_) {}
  }, [onGetUsersList]);

  useEffect(() => {
    if (activeTab === 'overview') {
      // ensure users loaded for selector
      if (users.length === 0) {
        loadUsers();
      }
      fetchDashboardStats();
    }
  }, [activeTab, fetchDashboardStats, loadUsers, users.length]);

  // Simulate real-time data updates
  useEffect(() => {
    const interval = setInterval(() => {
      setTradingData(prev => ({
        totalProfit: prev.totalProfit + (Math.random() - 0.5) * 100,
        dailyProfit: prev.dailyProfit + (Math.random() - 0.5) * 50,
        activeBots: Math.floor(Math.random() * 10) + 5,
        totalTrades: prev.totalTrades + Math.floor(Math.random() * 5),
        winRate: Math.random() * 100,
        balance: prev.balance + (Math.random() - 0.5) * 200
      }));
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    onLogout();
  };

  // Get user role display name
  const getUserRoleName = (userRole) => {
    switch (userRole) {
      case 1: return 'Trader';
      case 2: return 'Trader / Manager';
      case 3: return 'Admin';
      default: return 'User';
    }
  };

  // Get user status display name
  const getUserStatusName = (status) => {
    switch (status) {
      case 1: return 'Active';
      case 2: return 'Frozen';
      case 3: return 'Suspended';
      default: return 'Unknown';
    }
  };

  const StatCard = ({ title, value, change, icon, color }) => (
    <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 sm:p-6 flex items-center gap-3 sm:gap-4 hover:transform hover:-translate-y-1 hover:shadow-xl transition-all duration-300">
      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center text-xl sm:text-2xl" style={{ backgroundColor: color }}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-xs sm:text-sm text-dark-400 font-medium mb-1 sm:mb-2 truncate">{title}</h3>
        <div className="text-lg sm:text-2xl font-bold text-white mb-1 truncate">{value}</div>
        {change && (
          <div className={`text-xs sm:text-sm font-semibold ${change > 0 ? 'text-green-400' : 'text-red-400'}`}>
            {change > 0 ? '+' : ''}{change}%
          </div>
        )}
      </div>
    </div>
  );

  const TradingBot = ({ name, status, profit, pair }) => (
    <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 sm:p-5 hover:transform hover:-translate-y-1 transition-all duration-300">
      <div className="flex justify-between items-center mb-3">
        <h4 className="text-base sm:text-lg font-semibold text-white truncate">{name}</h4>
        <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-semibold uppercase flex-shrink-0 ${
          status === 'active' ? 'bg-green-500 text-white' : 'bg-yellow-500 text-white'
        }`}>
          {status}
        </span>
      </div>
      <div className="flex justify-between items-center">
        <span className="text-dark-400 font-medium text-sm truncate">{pair}</span>
        <span className={`font-bold text-base sm:text-lg ${profit >= 0 ? 'text-green-400' : 'text-red-400'} flex-shrink-0`}>
          {profit >= 0 ? '+' : ''}{profit.toFixed(2)}%
        </span>
      </div>
    </div>
  );

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'trades', label: 'Trades', icon: '📈' },
    { id: 'simulator', label: 'Simulator', icon: '🎮' },
    { id: 'simulator10s', label: 'Simulator 10s', icon: '⏱️' },
    { id: 'technicals', label: 'Technicals', icon: '📉' },
    { id: 'accounts', label: 'Accounts', icon: '💼' },
    { id: 'symbols', label: 'Symbols', icon: '📈' },
    { id: 'userTree', label: 'User Tree', icon: '🌳' },
    { id: 'users', label: 'Users', icon: '👥' },
    { id: 'settings', label: 'Settings', icon: '⚙️' }
  ];
  // Prevent non-admins from accessing userTree tab
  useEffect(() => {
    const isAdmin = currentUser?.userRole === 3;
    if (!isAdmin && activeTab === 'userTree') {
      setActiveTab('overview');
    }
  }, [activeTab, currentUser]);


  const getUserTree = useCallback(() => apiService.getUserTree(), []);

  return (
    <div className="min-h-screen bg-dark-900 text-dark-200">
      {/* Header */}
      <header className="flex justify-between items-center p-2 sm:p-3 bg-dark-800 border-b border-dark-700 shadow-lg">
        <div className="flex items-center gap-2 sm:gap-3">
          <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="Logo" className="w-10 h-10 sm:w-12 sm:h-12 object-contain" />
        </div>
        <div className="flex items-center gap-3 sm:gap-5">
          <div className="hidden sm:flex items-center gap-2">
            <span className="text-lg">👤</span>
            <div className="text-right">
              <div className="font-medium text-white text-sm">
                {currentUser?.userFirstName} {currentUser?.userLastName}
              </div>
              <div className="text-dark-400 text-xs">
                {getUserRoleName(currentUser?.userRole)}
              </div>
            </div>
          </div>
          <button 
            onClick={handleLogout} 
            className="bg-red-600 text-white px-3 sm:px-4 py-2 rounded-lg font-medium hover:bg-red-700 transition-colors shadow-md hover:shadow-lg text-sm touch-manipulation"
          >
            <span className="hidden sm:inline">Logout</span>
            <span className="sm:hidden">Exit</span>
          </button>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-dark-800 border-b border-dark-700 shadow-sm overflow-x-auto">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center space-x-1 sm:space-x-8 px-4 sm:px-8 min-w-max">
            {menuItems
              .filter(mi => (mi.id !== 'userTree' ? true : currentUser?.userRole === 3))
              .map((item) => (
              <button 
                key={item.id}
                className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-6 py-3 sm:py-4 font-medium text-xs sm:text-sm border-b-2 transition-all duration-300 relative group whitespace-nowrap touch-manipulation ${
                  activeTab === item.id 
                    ? 'text-primary-400 border-primary-400 bg-gradient-to-r from-primary-400/10 to-transparent' 
                    : 'text-dark-400 border-transparent hover:text-white hover:bg-dark-700/30'
                }`}
                onClick={() => setActiveTab(item.id)}
              >
                <span className="text-base sm:text-lg group-hover:scale-110 transition-transform duration-200">
                  {item.icon}
                </span>
                <span className="font-semibold">{item.label}</span>
                {activeTab === item.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-400 to-primary-600"></div>
                )}
                <div className={`absolute inset-0 rounded-lg transition-all duration-300 ${
                  activeTab === item.id 
                    ? 'bg-primary-400/5 border border-primary-400/20' 
                    : 'group-hover:bg-white/5'
                }`}></div>
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="p-4 sm:p-8 max-w-7xl mx-auto">
        {activeTab === 'overview' && (
          <div className="space-y-6 sm:space-y-8">
            {/* User Selector */}
            <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 sm:p-6 shadow-lg">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <label className="text-sm text-dark-300 font-medium">User</label>
                <select
                  className="bg-dark-700 border border-dark-600 rounded-lg px-3 py-2 text-sm text-white w-full sm:w-80 focus:outline-none focus:border-primary-500"
                  value={selectedUserId ?? currentUser?.userId ?? ''}
                  onChange={(e) => setSelectedUserId(Number(e.target.value))}
                >
                  {/* Default to logged in user if list not loaded */}
                  {users.length === 0 && (
                    <option value={currentUser?.userId || ''}>
                      {currentUser ? `${currentUser.userFirstName} ${currentUser.userLastName}` : 'Select User'}
                    </option>
                  )}
                  {users.map(u => (
                    <option key={u.userId} value={u.userId}>
                      {(u.userFirstName || '') + ' ' + (u.userLastName || '')} {u.userRole === 3 ? '(Admin)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {/* User Info Card */}
            <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 sm:p-6 shadow-lg">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 sm:mb-5">User Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-dark-700 rounded-lg p-3">
                  <div className="text-dark-400 text-xs sm:text-sm mb-1">User ID</div>
                  <div className="text-blue-400 font-medium text-sm sm:text-base">
                    {currentUser?.userId}
                  </div>
                </div>
                <div className="bg-dark-700 rounded-lg p-3">
                  <div className="text-dark-400 text-xs sm:text-sm mb-1">Name</div>
                  <div className="text-white font-medium text-sm sm:text-base">
                    {currentUser?.userFirstName} {currentUser?.userLastName}
                  </div>
                </div>
                <div className="bg-dark-700 rounded-lg p-3">
                  <div className="text-dark-400 text-xs sm:text-sm mb-1">Email</div>
                  <div className="text-white font-medium text-sm sm:text-base truncate">
                    {currentUser?.eMailId}
                  </div>
                </div>
                <div className="bg-dark-700 rounded-lg p-3">
                  <div className="text-dark-400 text-xs sm:text-sm mb-1">Role</div>
                  <div className="text-white font-medium text-sm sm:text-base">
                    {getUserRoleName(currentUser?.userRole)}
                  </div>
                </div>
                <div className="bg-dark-700 rounded-lg p-3">
                  <div className="text-dark-400 text-xs sm:text-sm mb-1">Status</div>
                  <div className={`font-medium text-sm sm:text-base ${
                    currentUser?.status === 1 ? 'text-green-400' : 
                    currentUser?.status === 2 ? 'text-yellow-400' : 'text-red-400'
                  }`}>
                    {getUserStatusName(currentUser?.status)}
                  </div>
                </div>
                <div className="bg-dark-700 rounded-lg p-3">
                  <div className="text-dark-400 text-xs sm:text-sm mb-1">Account ID</div>
                  <div className="text-purple-400 font-medium text-sm sm:text-base">
                    {currentUser?.accountID || 'Not Set'}
                  </div>
                </div>
                <div className="bg-dark-700 rounded-lg p-3">
                  <div className="text-dark-400 text-xs sm:text-sm mb-1">Manager ID</div>
                  <div className="text-yellow-400 font-medium text-sm sm:text-base">
                    {currentUser?.managerId || 'None'}
                  </div>
                </div>
                <div className="bg-dark-700 rounded-lg p-3">
                  <div className="text-dark-400 text-xs sm:text-sm mb-1">Login Token</div>
                  <div className="text-green-400 font-medium text-sm sm:text-base truncate">
                    {currentUser?.login_token ? 'Active' : 'Not Available'}
                  </div>
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              <StatCard
                title="Total PnL"
                value={`$${(dashboardStats?.totalPnl ?? 0).toFixed(2)}`}
                change={5.2}
                icon="💰"
                color="#10b981"
              />
              <StatCard
                title="30d PnL"
                value={`$${(dashboardStats?.totalPnlLast30 ?? 0).toFixed(2)}`}
                change={-2.1}
                icon="📊"
                color="#3b82f6"
              />
              <StatCard
                title="Active Bots"
                value={tradingData.activeBots}
                icon="🤖"
                color="#8b5cf6"
              />
              <StatCard
                title="Win Rate"
                value={`${dashboardStats ? ((dashboardStats.winningTrades / Math.max(1, dashboardStats.tradesCount)) * 100).toFixed(1) : '0.0'}%`}
                icon="🎯"
                color="#f59e0b"
              />
            </div>

            

            {/* PnL Over Time */}
            <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 sm:p-6 shadow-lg">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 sm:mb-5">PnL Over Time</h3>
              <div className="h-48">
                {dashboardStats && Array.isArray(dashboardStats.dailyPnl) && dashboardStats.dailyPnl.length > 0 ? (
                  <svg className="w-full h-full">
                    {(() => {
                      const pts = dashboardStats.dailyPnl;
                      const w = 800, h = 180, pad = 20;
                      const xs = pts.map(p=>p.t), ys = pts.map(p=>p.v);
                      const minX = Math.min(...xs), maxX = Math.max(...xs);
                      const minY = Math.min(...ys), maxY = Math.max(...ys);
                      const scaleX = (x)=> pad + ((x - minX) / Math.max(1, (maxX - minX))) * (w - pad*2);
                      const scaleY = (y)=> h - pad - ((y - minY) / Math.max(1, (maxY - minY))) * (h - pad*2);
                      const path = pts.map((p,i)=> `${i===0?'M':'L'} ${scaleX(p.t)} ${scaleY(p.v)}`).join(' ');
                      return (
                        <g>
                          <path d={path} stroke="#60a5fa" strokeWidth="2" fill="none" />
                        </g>
                      );
                    })()}
                  </svg>
                ) : (
                  <div className="text-dark-400">No data</div>
                )}
              </div>
            </div>

            {/* Symbol Performance */}
            <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 sm:p-6 shadow-lg">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 sm:mb-5">Symbol Performance</h3>
              {dashboardStats && Array.isArray(dashboardStats.symbolPerformance) && dashboardStats.symbolPerformance.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {dashboardStats.symbolPerformance.slice(0,9).map((s, idx) => (
                    <div key={s.s+idx} className="bg-dark-700 rounded-lg p-3 border border-dark-600">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-semibold text-sm">{s.s}</span>
                        <span className={`${s.v>=0?'text-green-400':'text-red-400'} text-sm font-semibold`}>{s.v.toFixed(2)}</span>
                      </div>
                      <div className="w-full h-2 bg-dark-600 rounded">
                        {(() => {
                          const maxAbs = Math.max(...dashboardStats.symbolPerformance.map(x=>Math.abs(x.v)), 1);
                          const pct = Math.min(100, Math.round(Math.abs(s.v) / maxAbs * 100));
                          return <div className={`${s.v>=0?'bg-green-500':'bg-red-500'} h-2 rounded`} style={{ width: pct + '%' }}></div>;
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-dark-400">No data</div>
              )}
            </div>
            <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 sm:p-6 shadow-lg">
              <h3 className="text-lg sm:text-xl font-semibold text-white mb-4 sm:mb-5">Recent Activity</h3>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center gap-3 p-3 bg-dark-700 rounded-lg hover:bg-dark-600 transition-colors">
                  <span className="text-lg sm:text-xl">✅</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm sm:text-base truncate">Bot "BTC Scalper" executed buy order</p>
                    <span className="text-dark-400 text-xs sm:text-sm">2 minutes ago</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-dark-700 rounded-lg hover:bg-dark-600 transition-colors">
                  <span className="text-lg sm:text-xl">💰</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm sm:text-base truncate">Profit target reached on ETH/USDT</p>
                    <span className="text-dark-400 text-xs sm:text-sm">5 minutes ago</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-dark-700 rounded-lg hover:bg-dark-600 transition-colors">
                  <span className="text-lg sm:text-xl">⚠️</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm sm:text-base truncate">Stop loss triggered on ADA/USDT</p>
                    <span className="text-dark-400 text-xs sm:text-sm">10 minutes ago</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'bots' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <h3 className="text-xl sm:text-2xl font-semibold text-white">Trading Bots</h3>
              <button className="bg-green-600 text-white px-4 sm:px-5 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors shadow-md hover:shadow-lg touch-manipulation text-sm sm:text-base w-full sm:w-auto">
                + Add New Bot
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              <TradingBot name="BTC Scalper" status="active" profit={12.5} pair="BTC/USDT" />
              <TradingBot name="ETH Momentum" status="active" profit={-3.2} pair="ETH/USDT" />
              <TradingBot name="ADA Swing" status="paused" profit={8.7} pair="ADA/USDT" />
              <TradingBot name="BNB Grid" status="active" profit={15.3} pair="BNB/USDT" />
            </div>
          </div>
        )}

        {activeTab === 'trades' && (
          <Trades
            onGetUsersList={onGetUsersList}
            onGetTradingAccountList={onGetTradingAccountList}
            currentUser={currentUser}
          />
        )}

        {activeTab === 'simulator' && (
          <Simulator
            currentUser={currentUser}
          />
        )}

        {activeTab === 'simulator10s' && (
          <Simulator10s />
        )}

        {activeTab === 'technicals' && (
          <Technicals
            currentUser={currentUser}
          />
        )}

        {activeTab === 'accounts' && (
          <TradingAccounts
            onCreateTradingAccount={onCreateTradingAccount}
            onGetTradingAccountList={onGetTradingAccountList}
            onGetTradingAccountListAll={onGetTradingAccountListAll}
            onDeleteTradingAccount={onDeleteTradingAccount}
            onGetUsersList={onGetUsersList}
            currentUser={currentUser}
          />
        )}

        {activeTab === 'symbols' && (
          <Symbols
            onCreateSymbol={onCreateSymbol}
            onUpdateSymbol={onUpdateSymbol}
            onGetSymbolList={onGetSymbolList}
            onGetSymbolListAll={onGetSymbolListAll}
            onDeleteSymbol={onDeleteSymbol}
            onGetUsersList={onGetUsersList}
            onGetTradingAccountList={onGetTradingAccountList}
            currentUser={currentUser}
          />
        )}

        {activeTab === 'userTree' && (
          <UserTree onGetUserTree={getUserTree} />
        )}

        {activeTab === 'users' && (
          <UserManagement
            onCreateUser={onCreateUser}
            onGetUsersList={onGetUsersList}
            onUpdateUser={onUpdateUser}
            currentUser={currentUser}
          />
        )}

        {activeTab === 'settings' && (
          <div>
            <h3 className="text-xl sm:text-2xl font-semibold text-white mb-4 sm:mb-6">Settings</h3>
            <div className="space-y-6">
              <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 sm:p-6 shadow-lg">
                <h4 className="text-base sm:text-lg font-semibold text-white mb-4 sm:mb-5">API Configuration</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">Binance API Key</label>
                    <input 
                      type="password" 
                      placeholder="Enter API Key" 
                      className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">Binance Secret Key</label>
                    <input 
                      type="password" 
                      placeholder="Enter Secret Key" 
                      className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                    />
                  </div>
                </div>
              </div>
              <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 sm:p-6 shadow-lg">
                <h4 className="text-base sm:text-lg font-semibold text-white mb-4 sm:mb-5">Risk Management</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">Max Position Size (%)</label>
                    <input 
                      type="number" 
                      placeholder="5" 
                      className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-dark-300 font-medium mb-2 text-sm sm:text-base">Stop Loss (%)</label>
                    <input 
                      type="number" 
                      placeholder="2" 
                      className="w-full px-3 sm:px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 text-sm sm:text-base"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
